import type { ReactNode } from "react";
import { Badge } from "./Badge.js";
import { Tooltip } from "./Tooltip.js";

/**
 * KP-1..KP-3: kafelki liczb.
 *
 * Dwie postaci, bo T-1/T-2 nie pozwalaja wlozyc kafelka do karty:
 *   KpiTile     - SAMODZIELNA karta poziomu 1 (--surface + hairline + cien). Stoi
 *                 wprost na washu, w siatce KpiGrid.
 *   KpiWartosc  - ten sam uklad BEZ wlasnej powierzchni. Do uzycia WEWNATRZ karty,
 *                 gdzie kafelek bylby karta w karcie (T-2).
 *
 * KP-1: slot trendu istnieje ZAWSZE, takze pusty (stala wysokosc). Bez tego pojawienie
 * sie badge'a trendu spycha liczbe w dol i wiersz kafelkow przestaje stac w jednej
 * linii (S-7).
 * KP-2: brak sygnalu to kreska "-" z wyjasnieniem w dymku, NIGDY zniknieta wartosc
 * ani liczba zmyslona.
 */

export interface Trend {
  /** Tekst badge'a, np. "+12%". */
  etykieta: string;
  kierunek: "wzrost" | "spadek";
}

interface WartoscProps {
  etykieta: string;
  /** Sformatowana wartosc. `null` = brak sygnalu -> kreska plus powod w dymku. */
  wartosc: string | null;
  /** Krotki sufiks przy liczbie (KP-2): "s", "%", "sl./min". */
  jednostka?: string;
  /** KP-2: pelna druga wartosc schodzi do OSOBNEJ linii, nie do nawiasu. */
  druga?: string;
  trend?: Trend;
  /** KP-2: czemu nie ma liczby. Widoczne w dymku przy kresce. */
  powodBraku?: string;
  /** true w trakcie ladowania - kreska bez dymka (S-7: bez skoku ukladu). */
  ladowanie?: boolean;
}

/** Uklad liczby bez wlasnej powierzchni - do wstawienia wewnatrz karty (T-2). */
export function KpiWartosc({
  etykieta,
  wartosc,
  jednostka,
  druga,
  trend,
  powodBraku,
  ladowanie = false,
}: WartoscProps): React.JSX.Element {
  const brak = ladowanie || wartosc === null;

  const liczba = (
    <span className={`kpi__value${brak ? " kpi__value--loading" : ""}`} aria-busy={ladowanie || undefined}>
      {brak ? "-" : wartosc}
    </span>
  );

  return (
    <>
      <p className="kpi__label">{etykieta}</p>
      <div className="kpi__trend">
        {trend && (
          <Badge wariant={trend.kierunek === "wzrost" ? "success" : "danger"}>{trend.etykieta}</Badge>
        )}
      </div>
      <div className="kpi__value-row">
        {brak && powodBraku && !ladowanie ? <Tooltip tekst={powodBraku}>{liczba}</Tooltip> : liczba}
        {!brak && jednostka && <span className="kpi__unit">{jednostka}</span>}
      </div>
      {druga && !brak && <span className="kpi__sub">{druga}</span>}
    </>
  );
}

/** KP-1: samodzielny kafelek. NIE wkladac do karty - to sama w sobie karta (T-2). */
export function KpiTile({ ikona, ...wartosc }: WartoscProps & { ikona?: ReactNode }): React.JSX.Element {
  return (
    <article className="kpi">
      {ikona && (
        <span className="kpi__icon-wrap" aria-hidden="true">
          {ikona}
        </span>
      )}
      <KpiWartosc {...wartosc} />
    </article>
  );
}

/** KP-3: rowne kafelki, auto-fit od ~200px, bez wypelniaczy. */
export function KpiGrid({ children }: { children: ReactNode }): React.JSX.Element {
  return <div className="kpi-grid">{children}</div>;
}
